import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Package,
  Calendar,
  User,
  CheckCircle,
  Clock,
  Download,
  AlertCircle,
  DollarSign
} from 'lucide-react'
import api from '../../services/api'

/**
 * Transfer Details Page
 * صفحة تفاصيل التحويل - عرض كامل تفاصيل التحويل مع الأصناف
 */
const TransferDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [transfer, setTransfer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [receivingTransfer, setReceivingTransfer] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    fetchTransferDetails()
  }, [id])

  const fetchTransferDetails = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get(`/mobile/transfers/${id}`)

      if (response.data.success) {
        setTransfer(response.data.data)
      } else {
        setError('فشل في تحميل تفاصيل التحويل')
      }
    } catch (err) {
      console.error('Error fetching transfer details:', err)
      setError(err.response?.data?.message || 'حدث خطأ أثناء تحميل التفاصيل')
    } finally {
      setLoading(false)
    }
  }

  const handleReceiveTransfer = async () => {
    setShowConfirmModal(false)

    try {
      setReceivingTransfer(true)
      const response = await api.post(`/mobile/transfers/${id}/receive`)

      if (response.data.success) {
        setShowSuccessModal(true)
        await fetchTransferDetails() // Refresh details
      } else {
        setErrorMessage(response.data.message || 'فشل في استلام التحويل')
        setShowErrorModal(true)
      }
    } catch (err) {
      console.error('Error receiving transfer:', err)
      setErrorMessage(err.response?.data?.message || 'حدث خطأ أثناء استلام التحويل')
      setShowErrorModal(true)
    } finally {
      setReceivingTransfer(false)
    }
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        label: 'قيد الانتظار',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: Clock
      },
      received: {
        label: 'تم الاستلام',
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle
      },
      completed: {
        label: 'تم الاستلام',
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle
      }
    }

    const config = statusConfig[status] || statusConfig.pending
    const Icon = config.icon

    return (
      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${config.color}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </div>
    )
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-EG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0)
  }

  const calculateTotalValue = () => {
    if (!transfer?.items) return 0
    return transfer.items.reduce((sum, item) => {
      const price = item.product?.sale_price || 0
      return sum + (item.quantity * price)
    }, 0)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 pb-24">
        <div className="max-w-lg mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">جاري تحميل التفاصيل...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !transfer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 pb-24">
        <div className="max-w-lg mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-red-800 font-medium">{error}</p>
            <div className="flex gap-2 justify-center mt-4">
              <button
                onClick={() => navigate('/transfers')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                رجوع
              </button>
              <button
                onClick={fetchTransferDetails}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                إعادة المحاولة
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const totalValue = calculateTotalValue()

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 pb-24">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/transfers')}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">تفاصيل التحويل</h1>
            <p className="text-sm text-gray-600">{transfer.transfer_number}</p>
          </div>
        </div>

        {/* Transfer Info Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-4">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-white font-semibold text-lg">{transfer.transfer_number}</span>
              {getStatusBadge(transfer.status)}
            </div>
          </div>

          <div className="p-4 space-y-4">
            {/* Warehouse */}
            {transfer.to_warehouse && (
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <Package className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">المخزن المستهدف</p>
                  <p className="text-sm font-semibold text-gray-900">{transfer.to_warehouse.name}</p>
                </div>
              </div>
            )}

            {/* Date */}
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <Calendar className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">تاريخ التحويل</p>
                <p className="text-sm font-semibold text-gray-900">
                  {new Date(transfer.transfer_date).toLocaleDateString('ar-EG', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>


            {/* Notes */}
            {transfer.notes && (
              <div className="text-sm text-gray-700 bg-blue-50 p-3 rounded-lg border border-blue-100">
                <p className="text-xs text-blue-600 font-medium mb-1">📝 ملاحظات</p>
                {transfer.notes}
              </div>
            )}
          </div>
        </div>

        {/* Items List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-4">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-3">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <Package className="w-5 h-5" />
              الأصناف ({transfer.items?.length || 0})
            </h2>
          </div>

          <div className="divide-y divide-gray-200">
            {transfer.items && transfer.items.length > 0 ? (
              transfer.items.map((item, index) => (
                <div key={item.id || index} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex gap-3">
                    {/* Product Image */}
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      {item.product?.image ? (
                        <img
                          src={item.product.image}
                          alt={item.product.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <Package className="w-8 h-8 text-gray-400" />
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {item.product?.name || 'منتج غير معروف'}
                      </h3>
                      <p className="text-xs text-gray-500 mb-2">
                        {item.product?.code || '-'}
                      </p>

                      {/* Quantity and Price */}
                      <div className="flex items-center gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">الكمية: </span>
                          <span className="font-semibold text-gray-900">
                            {item.quantity} {item.product?.unit || 'وحدة'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">السعر: </span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(item.product?.sale_price)} ج
                          </span>
                        </div>
                      </div>

                      {/* Item Total */}
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <span className="text-xs text-gray-500">الإجمالي: </span>
                        <span className="text-sm font-bold text-blue-600">
                          {formatCurrency(item.quantity * (item.product?.sale_price || 0))} ج
                        </span>
                      </div>

                      {/* Notes */}
                      {item.notes && (
                        <p className="text-xs text-gray-600 mt-2 bg-gray-50 p-2 rounded">
                          {item.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>لا توجد أصناف في هذا التحويل</p>
              </div>
            )}
          </div>
        </div>

        {/* Summary Card */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl shadow-lg p-4 mb-4 text-white">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              <span className="font-medium">الإجمالي الكلي</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-green-100">إجمالي الكمية:</span>
              <span className="text-xl font-bold">{transfer.total_quantity || 0} وحدة</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-green-100">إجمالي القيمة:</span>
              <span className="text-2xl font-bold">{formatCurrency(totalValue)} ج</span>
            </div>
          </div>
        </div>

        {/* Receive Button */}
        {transfer.status === 'pending' && (
          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={receivingTransfer}
            className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-lg transition-all ${
              receivingTransfer
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
            }`}
          >
            {receivingTransfer ? (
              <>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                جاري الاستلام...
              </>
            ) : (
              <>
                <CheckCircle className="w-6 h-6" />
                تأكيد الاستلام
              </>
            )}
          </button>
        )}

        {/* Received Info */}
        {(transfer.status === 'received' || transfer.status === 'completed') && transfer.received_date && (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 text-center">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
            <p className="text-green-800 font-bold mb-1">تم الاستلام بنجاح</p>
            <p className="text-sm text-green-600">
              تاريخ الاستلام: {new Date(transfer.received_date).toLocaleDateString('ar-EG', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scaleIn">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-t-2xl p-6 text-center">
              <div className="bg-white rounded-full w-20 h-20 mx-auto flex items-center justify-center mb-4">
                <Package className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-white">تأكيد الاستلام</h3>
            </div>

            <div className="p-6">
              <p className="text-gray-700 text-lg text-center mb-2">
                هل أنت متأكد من استلام هذا التحويل؟
              </p>
              <p className="text-gray-500 text-sm text-center mb-6">
                رقم التحويل: <span className="font-semibold text-blue-600">{transfer.transfer_number}</span>
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleReceiveTransfer}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg hover:shadow-xl"
                >
                  تأكيد
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scaleIn">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-t-2xl p-6 text-center">
              <div className="bg-white rounded-full w-20 h-20 mx-auto flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-white">تم الاستلام بنجاح!</h3>
            </div>

            <div className="p-6">
              <p className="text-gray-700 text-lg text-center mb-6">
                تم استلام التحويل وإضافة الأصناف إلى مخزنك
              </p>

              <button
                onClick={() => {
                  setShowSuccessModal(false)
                  navigate('/transfers')
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg hover:shadow-xl"
              >
                حسناً
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scaleIn">
            <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-t-2xl p-6 text-center">
              <div className="bg-white rounded-full w-20 h-20 mx-auto flex items-center justify-center mb-4">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-white">فشل الاستلام</h3>
            </div>

            <div className="p-6">
              <p className="text-gray-700 text-lg text-center mb-6">
                {errorMessage}
              </p>

              <button
                onClick={() => setShowErrorModal(false)}
                className="w-full px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl font-semibold hover:from-red-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl"
              >
                حسناً
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TransferDetails
